import { memo } from 'react';
import { PieChart } from 'react-gifted-charts';
import { Text as RNText, View } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import type { ColoredDataPoint, DonutChartProps } from './types';

export type { DonutChartProps };
export type { ColoredDataPoint };

function CenterLabel({
  centerValue,
  centerLabel,
}: Readonly<{ centerValue?: string; centerLabel?: string }>) {
  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      {centerValue && (
        <RNText
          style={{
            color: colors.text.primary,
            fontFamily: darkPrecisionFontFamilies.metricBold,
            fontSize: 32,
            fontWeight: '600',
            lineHeight: 32,
          }}
        >
          {centerValue}
        </RNText>
      )}
      {centerLabel && (
        <RNText
          style={{
            color: colors.text.muted,
            fontSize: tokens.fontSize.xs,
            fontWeight: '600',
            letterSpacing: 1,
            textTransform: 'uppercase',
            marginTop: 2,
          }}
        >
          {centerLabel}
        </RNText>
      )}
    </View>
  );
}

function ChartLegend({ data, total }: Readonly<{ data: ColoredDataPoint[]; total: number }>) {
  return (
    <View style={{ marginTop: tokens.spacing.md, alignSelf: 'stretch', width: '100%' }}>
      {data.map((point) => {
        const percentage = ((point.value / total) * 100).toFixed(1);
        return (
          <View
            key={point.label}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: tokens.spacing.xs,
            }}
          >
            <View
              style={{
                width: 10,
                height: 10,
                borderRadius: tokens.radius.full,
                backgroundColor: point.color,
                marginRight: tokens.spacing.sm,
              }}
            />
            <RNText
              style={{
                flex: 1,
                color: colors.text.secondary,
                fontSize: tokens.fontSize.sm,
              }}
            >
              {point.label}
            </RNText>
            <RNText
              style={{
                color: colors.text.muted,
                fontSize: tokens.fontSize.sm,
                fontWeight: '600',
              }}
            >
              {percentage}%
            </RNText>
          </View>
        );
      })}
    </View>
  );
}

function DonutChartInner({
  data,
  size = 160,
  strokeWidth: ringWidth = 24,
  innerRadius: propInnerRadius,
  showLegend = true,
  centerLabel,
  centerValue,
  animate = true,
  style,
}: Readonly<DonutChartProps>) {
  if (!data || data.length === 0) {
    return <View style={[{ width: size, height: size }, style]} />;
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total <= 0) {
    return <View style={[{ width: size, height: size }, style]} />;
  }

  const radius = size / 2;
  const computedInnerRadius = propInnerRadius ?? radius - ringWidth;

  const giftedData = data.map((point) => ({
    value: point.value,
    color: point.color,
    text: point.label,
  }));

  const chartLabel = `Donut chart: ${data.length} segments, total ${total}`;

  return (
    <View
      style={[{ alignItems: 'center' }, style]}
      accessibilityRole="image"
      accessibilityLabel={chartLabel}
    >
      <PieChart
        data={giftedData}
        donut
        radius={radius}
        innerRadius={computedInnerRadius}
        innerCircleColor={colors.background.primary}
        backgroundColor="transparent"
        isAnimated={animate}
        animationDuration={600}
        focusOnPress
        centerLabelComponent={() => (
          <CenterLabel centerValue={centerValue} centerLabel={centerLabel} />
        )}
        textColor={colors.text.secondary}
        textSize={tokens.fontSize.xs}
      />
      {showLegend && <ChartLegend data={data} total={total} />}
    </View>
  );
}

export const DonutChart = memo(DonutChartInner);
